import { useEffect, useRef, useState } from "react";
import { gamesApi } from "@/lib/api";
import ArcadeShell from "@/components/games/ArcadeShell";

const COLS = 10;
const ROWS = 16;
const CELL = 20;
const SHAPES = {
  I: [[1, 1, 1, 1]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1]],
  L: [[1, 0], [1, 0], [1, 1]],
  J: [[0, 1], [0, 1], [1, 1]],
  S: [[0, 1, 1], [1, 1, 0]],
  Z: [[1, 1, 0], [0, 1, 1]],
};
const COLORS = { I: "#438263", O: "#D6A419", T: "#2E5C45", L: "#B5651D", J: "#1B4F72", S: "#34C759", Z: "#FF3B30" };
const KEYS = Object.keys(SHAPES);

const emptyGrid = () => Array.from({ length: ROWS }, () => Array(COLS).fill(null));
const rotate = (m) => m[0].map((_, i) => m.map((row) => row[i]).reverse());
const randPiece = () => {
  const k = KEYS[Math.floor(Math.random() * KEYS.length)];
  return { k, shape: SHAPES[k], x: Math.floor(COLS / 2) - 1, y: 0 };
};

function BlockBoard({ onGameOver, speed }) {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const gridRef = useRef(emptyGrid());
  const pieceRef = useRef(randPiece());
  const overRef = useRef(false);
  const dropInterval = { slow: 700, normal: 550, fast: 380 }[speed] || 550;

  const collides = (shape, px, py) => {
    for (let r = 0; r < shape.length; r++)
      for (let c = 0; c < shape[r].length; c++) {
        if (!shape[r][c]) continue;
        const nx = px + c, ny = py + r;
        if (nx < 0 || nx >= COLS || ny >= ROWS) return true;
        if (ny >= 0 && gridRef.current[ny][nx]) return true;
      }
    return false;
  };

  const merge = () => {
    const p = pieceRef.current;
    p.shape.forEach((row, r) => row.forEach((v, c) => { if (v && p.y + r >= 0) gridRef.current[p.y + r][p.x + c] = p.k; }));
    // clear lines
    let cleared = 0;
    gridRef.current = gridRef.current.filter((row) => {
      if (row.every((cell) => cell)) { cleared += 1; return false; }
      return true;
    });
    while (gridRef.current.length < ROWS) gridRef.current.unshift(Array(COLS).fill(null));
    if (cleared) { scoreRef.current += cleared * 10; setScore(scoreRef.current); }
    const np = randPiece();
    if (collides(np.shape, np.x, np.y)) {
      overRef.current = true;
      onGameOver(scoreRef.current);
      return;
    }
    pieceRef.current = np;
  };

  const step = () => {
    const p = pieceRef.current;
    if (!collides(p.shape, p.x, p.y + 1)) p.y += 1;
    else merge();
  };

  const act = (type) => {
    if (overRef.current) return;
    const p = pieceRef.current;
    if (type === "left" && !collides(p.shape, p.x - 1, p.y)) p.x -= 1;
    else if (type === "right" && !collides(p.shape, p.x + 1, p.y)) p.x += 1;
    else if (type === "down") step();
    else if (type === "rotate") {
      const r = rotate(p.shape);
      if (!collides(r, p.x, p.y)) p.shape = r;
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowLeft", "a"].includes(e.key)) act("left");
      else if (["ArrowRight", "d"].includes(e.key)) act("right");
      else if (["ArrowDown", "s"].includes(e.key)) act("down");
      else if (["ArrowUp", "w"].includes(e.key)) act("rotate");
      if (e.key.startsWith("Arrow")) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#F4F1EA";
      ctx.fillRect(0, 0, COLS * CELL, ROWS * CELL);
      gridRef.current.forEach((row, r) => row.forEach((v, c) => {
        if (v) { ctx.fillStyle = COLORS[v]; ctx.fillRect(c * CELL + 1, r * CELL + 1, CELL - 2, CELL - 2); }
      }));
      const p = pieceRef.current;
      ctx.fillStyle = COLORS[p.k];
      p.shape.forEach((row, r) => row.forEach((v, c) => { if (v) ctx.fillRect((p.x + c) * CELL + 1, (p.y + r) * CELL + 1, CELL - 2, CELL - 2); }));
    };
    const tick = setInterval(() => { if (!overRef.current) { step(); draw(); } }, dropInterval);
    const render = setInterval(draw, 60);
    return () => { clearInterval(tick); clearInterval(render); };
  }, [dropInterval]);

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="font-display text-lg font-bold">Score: {score}</p>
      <canvas ref={canvasRef} width={COLS * CELL} height={ROWS * CELL} className="rounded-xl border border-border" data-testid="block-canvas" />
      <div className="grid grid-cols-3 gap-2" style={{ width: 180 }}>
        <span />
        <button onClick={() => act("rotate")} className="rounded-lg bg-secondary py-3 font-bold">⟳</button>
        <span />
        <button onClick={() => act("left")} className="rounded-lg bg-secondary py-3 font-bold">◀</button>
        <button onClick={() => act("down")} className="rounded-lg bg-secondary py-3 font-bold">▼</button>
        <button onClick={() => act("right")} className="rounded-lg bg-secondary py-3 font-bold">▶</button>
      </div>
      <p className="text-xs text-muted-foreground">Arrow keys / WASD: move & rotate. Clear lines to score.</p>
    </div>
  );
}

export default function BlockGame() {
  return (
    <ArcadeShell title="Block Puzzle" game="block" stateKey="blockState" stateFn={gamesApi.blockState} unlockFn={gamesApi.blockUnlock} scoreFn={gamesApi.blockScore}>
      {({ onGameOver, speed }) => <BlockBoard onGameOver={onGameOver} speed={speed} />}
    </ArcadeShell>
  );
}
